import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { NewSelect, NewInputList, ModalLvl2 } from "../..";
import { NewFieldStyles, SelectFieldOption_Values, Section, ListStyle_Types } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { useDeviceInfo, TerminalTypesInfo } from "../../../Services";
import { Station, StationTerminal } from "../../../Definitions/newSectionModalInterfaces";
import { useModalContext } from "../../LocationFeeder/_LocationFeeder/newSectionModalContext";

const stationsWithFeederInput = (Section_stations: Station[]) => Section_stations.flatMap((station => Object.values(station.terminals))).filter((s) => s.__terminalType === "feederOutput" && (s?.terminalConfig?.sensor_p !== null && s?.terminalConfig?.sensor_p !== undefined)).length;
const stationsWithFeederOutput = (Section_stations: Station[]) => Section_stations.flatMap((station => Object.values(station.terminals))).filter((s) => s.__terminalType === "feederOutput" && (s?.port !== null && s?.port !== undefined)).length;

const stationsWithGateInput = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => (s["gateMotorOutput"]?.terminalConfig?.sensor_p !== undefined && s["gateMotorOutput"]?.terminalConfig?.sensor_p !== null)).length;
const stationsWithGateOutput = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => s["gateMotorOutput"]?.port != null && s["gateMotorOutput"]?.port !== undefined).length;
const stationsWithGateSensor = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => s["gateSensor"]?.port !== null && s["gateSensor"]?.port !== undefined).length;
const stationsWithReader = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => s["readerInput"]?.port !== null && s["readerInput"]?.port !== undefined).length;

const stationsWithWaterOutput = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => s["waterOutput"]?.port !== null && s["waterOutput"]?.port !== undefined).length;
const stationsWithButtonInput = (Section_stations: Station[]) => Section_stations.map((station => station.terminals)).filter((s) => s["buttonInput"]?.port !== null && s["buttonInput"]?.port !== undefined).length;

const defaultValues = (station: Station, withWater: boolean, withButton: boolean, withGate: boolean) => {
  let content = [];

  if (withWater) content.push(station.terminals["waterOutput"]?.port ?? 0);
  if (withButton) content.push((station.terminals["buttonInput"]?.port ? (station.terminals["buttonInput"]?.port ?? 0) - 100 : 0));
  if (withGate) {
    content.push(station.terminals["gateMotorOutput"]?.port ?? 0);
    content.push(station.terminals["gateMotorOutput"]?.terminalConfig?.sensor_p ? (station.terminals["gateMotorOutput"]?.terminalConfig?.sensor_p ?? 0) - 100 : 0);
    content.push(station.terminals["gateSensor"]?.port ? station.terminals["gateSensor"]?.port - 100 : 0);
  }

  return content;
}

const NewEsfWfSectionModalLvl2: React.FC = () => {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);

  // #region | States
  const { state: { domain, connection, section }, dispatch } = useModalContext()
  const terminalTypesState = useAppSelector(({ graphQlData }) => (graphQlData.domain.terminalTypes.entities))
  // #endregion

  const _gate = (section.gate === true || section.gate === undefined) ? true : false;
  
  // #region | GraphQl
  const { data: deviceInfoState, call: getDeviceInfo } = useDeviceInfo(connection);

  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({ id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1 }))
    }
    return []
  }, [deviceInfoState, domain])

  useEffect(() => {
    getDeviceInfo({ fetchAmount: 500 }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const terminalTypes = useMemo(() => { return terminalTypesState[domain]?.data ?? [] }, [domain, terminalTypesState])
  const { input: removedBySpreadOperator, ...readerInputConfig } = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => terminalTypes.terminal_type_id === 200)!
  const { input: removedBySpreadOperatorMotor, ...motorConfig } = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => terminalTypes.terminal_type_id === 83)!
  const { input: removedBySpreadOperatorGate, ...gateConfig } = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => terminalTypes.terminal_type_id === 84)!
  const { input: removedBySpreadOperatorSensor, ...digitalInputConfig } = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => terminalTypes.terminal_type_id === 5)!
  const { input: removedBySpreadOperatorWater, ...waterConfig } = terminalTypes.find((terminalTypes: { terminal_type_id: number; }) => terminalTypes.terminal_type_id === 53)!

  const stations = { ...section, contents: section.contents as Station[] };
  // #endregion

  // #region | Remaining outputs/inputs

  // HJÆÆÆÆÆÆÆLP HER FREDAG!!!!! 
  const activeStationsCount = stations.contents.length;

  const totalOutputs = activeStationsCount * ((stations.feeder ?? 0) + (_gate ? 1 : 0) + (stations.water ? 1 : 0)) // feeders + gate motor output + water output (if water is selected)
  const totalInputs = activeStationsCount * ((stations.feeder ?? 0) + (_gate ? 2 : 0) + 1 + (section.button ? 1 : 0)) // feeders + gate motor sensor + gate motor input + reader + button input (if button is selected)

  const mainOutput = stationsWithFeederOutput(stations.contents) + (stationsWithGateOutput(stations.contents))
  const mainInput = (stationsWithFeederInput(stations.contents)) + (stationsWithGateInput(stations.contents)) + (stationsWithGateSensor(stations.contents)) + stationsWithReader(stations.contents)

  const remainingOutputs =
    section.water ? (totalOutputs - (mainOutput + (stationsWithWaterOutput(stations.contents))))
      : (totalOutputs - mainOutput)

  const remainingInputs =
    section.button ? (totalInputs - (mainInput + (stationsWithButtonInput(stations.contents))))
      : (totalInputs - mainInput)
  // #endregion

  // SelectField Options
  const ioOptions = deviceInfo.map((type) => ({
    label: type.name,
    id: type.id
  }));

  // Handle IO Change
  const handleStationIOChange = ({ stationId, value, keys, terminalOrder }: { stationId: string; value?: SelectFieldOption_Values; keys: (StationTerminal["__terminalType"] | string)[], terminalOrder?: number }) => {
    const updatedStations = stations.contents.map((station) => {
      if (station.stationId !== stationId) {
        return station;
      }
      keys.forEach((key) => station.terminals[key] = { ...station.terminals[key], io: Number(value?.id), terminalOrder: terminalOrder, __terminalType: terminalOrder !== undefined ? "feederOutput" : key as StationTerminal["__terminalType"] });
      return { ...station, terminals: { ...station.terminals } };
    });
    dispatch({ section: { contents: updatedStations } })
  };

  // Handle Value Changes from inputfields
  const handleValueChange = (stationIndex: number, newValue: string, counterKey: StationTerminal["__terminalType"], terminalConfig: Omit<TerminalTypesInfo, "input"> & { "sensor_p"?: number, terminal_type_id: number }, feederIndex?: number, assignmentKey?: StationTerminal["__terminalType"]) => {
    const updatedStations = stations.contents.map((station, index) => {
      if (index === stationIndex) {
        const oldTerminal = station.terminals[feederIndex === undefined ? counterKey : `${counterKey}-${feederIndex}`] as StationTerminal | undefined;
        const newTerminal = { ...oldTerminal, terminalConfig: { ...oldTerminal?.terminalConfig, ...terminalConfig }, terminalType: terminalConfig.terminal_type_id, port: newValue && assignmentKey === undefined ? Number(newValue) : oldTerminal?.port, terminalOrder: feederIndex ?? null, __terminalType: counterKey } as StationTerminal;

        station.terminals[feederIndex === undefined ? counterKey : `${counterKey}-${feederIndex}`] = newTerminal;
        return {
          ...station,
        };
      }
      return station;
    });
    dispatch({ section: { contents: updatedStations } })
  };

  // #region | Placeholders for inputfields
  const placeholdersContent = () => {
    let content = [];

    if (section.button) content.push("");
    if (section.water) content.push("");

    return content;
  }

  const headersContent = () => {
    let content = [];

    if (section.water) content.push(t("esfWalkThroughPages.newSection.waterOutput", { defaultValue: "Water Output" }));
    if (section.button) content.push(t("esfWalkThroughPages.newSection.buttonInput", { defaultValue: "Button Input" }));

    return content;
  }
  // #endregion

  // #region | IO Fields
  // TODO selection of terminal types
  const ioFields = stations.contents.filter((station) => station.stationId !== null).map((station, index) => {
    const currentPlaceholders = placeholdersContent();
    const currentHeaders = headersContent();
    const currentDefaultValues = defaultValues(station, section.water ?? false, section.button ?? false, _gate);

    const feedersFields = <>{Array.from({ length: stations.feeder ?? 0 }).map((_, feederIndex) => (
      <div style={{ display: "grid", gridTemplateColumns: "180px 2px 373px", gap: "10px" }}>
        <NewSelect
          key={"select_" + station.stationId}
          header={`Feeder ${feederIndex + 1} I/0`}
          placeholder={t("devices.chooseIo", { defaultValue: "Choose I/O" })}
          options={ioOptions}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary,
            fieldWidth: mobileXs ? "100%" : "180px"
          }}
          onChange={(selectedOption) => { if (station.stationId) { handleStationIOChange({ stationId: station.stationId, value: selectedOption, keys: [`feederOutput-${feederIndex}`], terminalOrder: feederIndex }) } }} // Single terminal for input and output motor
          valueOverrides={{
            defaultValue: ioOptions.find(option => option.id === station.terminals[`feederOutput-${feederIndex}`]?.io)
          }}
        />
        <div style={{ borderLeft: "2px solid " + colors.greyTertiary }} />
        <NewInputList
          key={`feeder_${feederIndex}`}
          placeholders={["", ""]}
          headers={[
            `${t("esfWalkThroughPages.newSection.feederOutput", { defaultValue: "Feeder Output" })} ${feederIndex + 1}`,
            `${t("esfWalkThroughPages.newSection.feederInput", { defaultValue: "Feeder Input" })} ${feederIndex + 1}`,
          ]}
          onChanges={[
            (newValue) =>
              handleValueChange(
                index,
                newValue,
                "feederOutput",
                { ...motorConfig, time_act: 3, val1_max: 2000 },
                feederIndex
              ),
            (newValue) =>
              handleValueChange(
                index,
                newValue,
                "feederOutput", // Value is set on output terminal
                {
                  ...motorConfig,
                  time_act: 3,
                  val1_max: 2000,
                  sensor_p: newValue ? Number(newValue) + 100 : undefined,
                },
                feederIndex,
                "feederInput"
              ),

          ]}
          fieldOverrides={{
            fieldStyle: NewFieldStyles.greyTertiary,
            fieldWidth: mobileXs ? "100%" : "180px",
          }}
          valueOverrides={{
            defaultValues: [
              stations.contents[index].terminals[`feederOutput-${feederIndex}`]?.port ?? 0,
              stations.contents[index].terminals[`feederOutput-${feederIndex}`]?.terminalConfig?.sensor_p ? (stations.contents[index].terminals[`feederOutput-${feederIndex}`]?.terminalConfig?.sensor_p ?? 0) - 100 : 0,
            ],
            valueTypes: ["number", "number"],
            minValueNumbers: [1, 1],
          }}
          listOverrides={{
            listStyle: ListStyle_Types.horizontal,
            listGap: "9px",
          }}
        />
      </div>
    ))}</>

    const gateFields = ( _gate ? <div style={{ display: "grid", gridTemplateColumns: "180px 2px 373px", gap: "10px" }}>
      <NewSelect
        key={"select_" + station.stationId}
        header="Gate I/0"
        placeholder={t("devices.chooseIo", { defaultValue: "Choose I/O" })}
        options={ioOptions}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px"
        }}
        onChange={(selectedOption) => { if (station.stationId) { handleStationIOChange({ stationId: station.stationId, value: selectedOption, keys: ["gateSensor", "gateMotorOutput"] }) } }} // Single terminal for input and output motor
        valueOverrides={{
          defaultValue: ioOptions.find(option => option.id === station.terminals["gateMotorOutput"]?.io)
        }}
      />
      <div style={{ borderLeft: "2px solid " + colors.greyTertiary }} />
      <NewInputList
        placeholders={["", "", ""]}
        headers={[
          t("esfWalkThroughPages.newSection.gateMotorOutput", { defaultValue: "Gate Motor Output" }),
          t("esfWalkThroughPages.newSection.gateMotorInput", { defaultValue: "Gate Motor Input" }),
          t("esfWalkThroughPages.newSection.gateSensor", { defaultValue: "Gate Sensor" })
        ]}
        onChanges={[
          (newValue) => handleValueChange(index, newValue, "gateMotorOutput", { ...gateConfig, time_act: 16, val1_max: 2000 }),
          (newValue) => handleValueChange(index, newValue, "gateMotorOutput", { ...gateConfig, time_act: 16, val1_max: 2000, sensor_p: newValue ? Number(newValue) + 100 : undefined }, undefined, "gateMotorInput"), // set on motor config
          (newValue) => handleValueChange(index, String(Number(newValue) + 100), "gateSensor", digitalInputConfig)
        ]}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "181px"
        }}
        valueOverrides={{
          defaultValues: [
            stations.contents[index].terminals["gateMotorOutput"]?.port ?? 0,
            stations.contents[index].terminals["gateMotorOutput"]?.terminalConfig?.sensor_p ? (stations.contents[index].terminals["gateMotorOutput"]?.terminalConfig?.sensor_p ?? 0) - 100 : 0,
            stations.contents[index].terminals["gateSensor"]?.port ? stations.contents[index].terminals["gateSensor"].port - 100 : 0],
          valueTypes: ["number", "number", "number"],
          minValueNumbers: [1, 1, 1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.group,
          listGap: "10px"
        }}
      />
    </div> : <></>);

    const additonalFieldKeys: string[] = []
    const additionalFieldsOnChange: ((newValue: string) => void)[] = []
    if (stations.water) {
      additonalFieldKeys.push('waterOutput')
      additionalFieldsOnChange.push(
        (newValue) => handleValueChange(index, newValue, "waterOutput", waterConfig),
      )
    }
    if (stations.button) {
      additonalFieldKeys.push('buttonInput')
      additionalFieldsOnChange.push(
        (newValue) => handleValueChange(index, String(Number(newValue) + 100), "buttonInput", { ...digitalInputConfig, time_act: 5 })
      )
    }
    const additionalFields = ((stations.button === true || stations.water === true) ? <div style={{ display: "grid", gridTemplateColumns: "180px 2px 373px", gap: "10px" }}>
      <NewSelect
        key={"select_" + station.stationId}
        header="additional I/0"
        placeholder={t("devices.chooseIo", { defaultValue: "Choose I/O" })}
        options={ioOptions}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px"
        }}
        onChange={(selectedOption) => { if (station.stationId) { handleStationIOChange({ stationId: station.stationId, value: selectedOption, keys: additonalFieldKeys }) } }}
        valueOverrides={{
          defaultValue: ioOptions.find(option => option.id === station.terminals["buttonInput"]?.io)
        }}
      />
      <div style={{ borderLeft: "2px solid " + colors.greyTertiary }} />
      <NewInputList
        placeholders={currentPlaceholders}
        headers={currentHeaders}
        onChanges={additionalFieldsOnChange}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px"
        }}
        valueOverrides={{
          defaultValues: currentDefaultValues,
          valueTypes: ["number", "number"],
          minValueNumbers: [1, 1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.horizontal,
          listGap: "9px"
        }}
      />
    </div> : <></>);

    const rfidFields = <div style={{ display: "grid", gridTemplateColumns: "180px 2px 373px", gap: "10px" }}>
      <NewSelect
        key={`select_${station.stationId}_reader`}
        header={`${t("motorSpecs.terminalTypes.rfidReader", { defaultValue: "RFID Reader" })}`}
        placeholder={t("devices.chooseRfidIo", { defaultValue: "Choose RFID I/O" })}
        options={ioOptions} // filter on io types at some point
        onChange={(selectedOption) => { if (station.stationId) { handleStationIOChange({ stationId: station.stationId, value: selectedOption, keys: ["readerInput"] }) } }}
        valueOverrides={{
          defaultValue: ioOptions.find(option => option.id === station.terminals["readerInput"]?.io)
        }}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px"
        }}
      />
      <div style={{ borderLeft: "2px solid " + colors.greyTertiary }} />
      <NewInputList
        placeholders={[""]}
        headers={[t("motorSpecs.terminalTypes.rfidReaderInput", { defaultValue: "RFID Reader Input" })]}
        onChanges={[
          (newValue) => handleValueChange(index, String(Number(newValue) + 200), "readerInput", readerInputConfig),
        ]}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldWidth: mobileXs ? "100%" : "180px"
        }}
        valueOverrides={{
          defaultValues: [stations.contents[index].terminals["readerInput"]?.port ? (stations.contents[index].terminals["readerInput"]?.port) - 200 : 0],
          valueTypes: ["number"],
          minValueNumbers: [1]
        }}
        listOverrides={{
          listStyle: ListStyle_Types.horizontal,
          listGap: "9px"
        }}
      />
    </div>

    return (<>
      <div key={station.stationId} style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
        <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{`${t("esfWalkThroughPages.sections.station", { defaultValue: "Station" })} ${station.name}`}</p>
        <div style={{
          width: "calc(100% - 20px)",
          height: "fit-content",
          backgroundColor: colors.greyLightSecondary,
          padding: "10px 10px",
          borderRadius: "10px", display: "flex", flexDirection: "column", gap: "20px"
        }}>
          {feedersFields}
          {gateFields}
          {additionalFields}
          {rfidFields}
        </div>
      </div>
    </>);
  })
  // #endregion

  return (
    <ModalLvl2
      content={ioFields}
      remainingInputs={remainingInputs}
      remainingOutputs={remainingOutputs}
    />
  );
};

export default NewEsfWfSectionModalLvl2;
